import { useCallback, useEffect } from 'react';
import { useFetcher } from 'react-router';

import { useHardRedirect } from '@jane/bloom/hooks/useHardRedirect';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import type {
  LoginUserResponse,
  Payload,
} from '@jane/bloom/routes/api.login/types';
import { trackLoggedIn } from '@jane/bloom/services/tracking/track';
import { buildFormData } from '@jane/bloom/utils/formData';

interface UseLoginArgs {
  /**
   * When provided, called on successful login instead of performing a hard
   * redirect. The fetcher still revalidates loaders (refreshing the
   * authenticated session), so the caller can keep the user on the page —
   * e.g. to open a follow-up modal. Without it, login hard-redirects as before.
   */
  onSuccess?: (result: { redirectTo: string }) => void;
  skipBagAfterAuth?: boolean;
}

export const useLogin = ({
  onSuccess,
  skipBagAfterAuth,
}: UseLoginArgs = {}) => {
  const { featureFlags, googleSsoClientId } = useRootLoaderData();
  const hardRedirect = useHardRedirect();
  const fetcher = useFetcher<LoginUserResponse>();

  const login = useCallback(
    (payload: Omit<Payload, 'skipBag'>) => {
      return fetcher.submit(
        buildFormData({
          ...payload,
          skipBag: skipBagAfterAuth ? `${skipBagAfterAuth}` : undefined,
        }),
        {
          action: `/api/login`,
          method: 'post',
          preventScrollReset: true,
        }
      );
    },
    [fetcher, skipBagAfterAuth]
  );

  useEffect(() => {
    if (fetcher.state === 'idle' && fetcher.data?.success) {
      trackLoggedIn(
        {
          googleSsoEnabled: !!googleSsoClientId,
          method: 'email',
          source: fetcher.data.payload.authSource,
        },
        featureFlags
      );
      if (onSuccess) {
        onSuccess({ redirectTo: fetcher.data.payload.redirectTo });
      } else {
        hardRedirect(fetcher.data.payload.redirectTo);
      }
    }
  }, [
    featureFlags,
    fetcher.state,
    fetcher.data,
    googleSsoClientId,
    hardRedirect,
    onSuccess,
  ]);

  return {
    fetcher,
    login,
  };
};
